import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const PrivacyPage = () => {
  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-navy-600 to-navy-700 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-white" />
            <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
          </div>
          <p className="text-navy-200 mt-2">Last updated: January 6, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="prose prose-slate max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
              <p className="text-blue-800 mb-0">
                <strong>Your Privacy Matters.</strong> At My Insurance Coverage, we are committed to protecting your personal information and being transparent about how we collect, use, and share your data.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">1. Information We Collect</h2>
            <p className="text-slate-600 mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>Personal Information:</strong> Name, date of birth, Social Security Number (when required for quotes), driver's license number</li>
              <li><strong>Contact Information:</strong> Email address, phone number, mailing address</li>
              <li><strong>Property Information:</strong> Home address, property details, vehicle information</li>
              <li><strong>Financial Information:</strong> Payment information for policy premiums</li>
              <li><strong>Health Information:</strong> Medical history (for life insurance applications only)</li>
            </ul>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Provide insurance quotes and policy recommendations</li>
              <li>Process and service your insurance applications</li>
              <li>Communicate with you about your policies and coverage options</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send important notices and policy updates</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Prevent fraud and protect our business</li>
            </ul>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">3. Information Sharing</h2>
            <p className="text-slate-600 mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>Insurance Carriers:</strong> To obtain quotes and bind coverage on your behalf</li>
              <li><strong>Service Providers:</strong> Third parties who assist us in operating our business</li>
              <li><strong>Regulatory Bodies:</strong> When required by law or to comply with legal processes</li>
              <li><strong>Business Partners:</strong> With your consent, for additional services you may request</li>
            </ul>
            <p className="text-slate-600 mb-6">
              We do not sell your personal information to third parties for marketing purposes.
            </p>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">4. Data Security</h2>
            <p className="text-slate-600 mb-6">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Encryption of sensitive data in transit and at rest</li>
              <li>Secure access controls and authentication</li>
              <li>Regular security assessments and updates</li>
              <li>Employee training on data protection</li>
            </ul>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">5. Cookies and Tracking</h2>
            <p className="text-slate-600 mb-6">
              Our website uses cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. You can control cookie preferences through your browser settings. Note that disabling cookies may affect the functionality of our website.
            </p>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">6. Your Rights</h2>
            <p className="text-slate-600 mb-4">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Request a copy of your data in a portable format</li>
            </ul>
            <p className="text-slate-600 mb-6">
              To exercise these rights, please contact us using the information provided below.
            </p>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 mb-6">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. Insurance records may be retained for extended periods as required by state regulations.
            </p>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-600 mb-6">
              Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information.
            </p>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-slate-600 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>

            <h2 className="text-2xl font-bold text-navy-900 mb-4">10. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 p-6 rounded-xl">
              <p className="text-slate-700"><strong>My Insurance Coverage</strong></p>
              <p className="text-slate-600">Phone: (201) 227-6413</p>
              <p className="text-slate-600">Email: bgreenberg@acrisure.com</p>
            </div>

            <div className="mt-8 p-6 bg-emerald-50 border border-emerald-200 rounded-xl">
              <h3 className="text-lg font-semibold text-emerald-800 mb-2">New Jersey Consumer Rights</h3>
              <p className="text-emerald-700 mb-0">
                New Jersey residents have specific rights under state law regarding the collection and use of personal information. For more information about your rights, please contact us or visit the New Jersey Division of Consumer Affairs website.
              </p>
            </div>
          </div>
        </div>

        {/* Back to top */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-navy-600 text-white font-semibold rounded-full hover:bg-navy-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
